import * as React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import MuiAppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import CssBaseline from '@mui/material/CssBaseline'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AccountCircleTwoToneIcon from '@mui/icons-material/AccountCircleTwoTone'
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone'
import BookmarkTwoToneIcon from '@mui/icons-material/BookmarkTwoTone'
import TrendingUpTwoToneIcon from '@mui/icons-material/TrendingUpTwoTone'
import LogoutTwoToneIcon from '@mui/icons-material/LogoutTwoTone'
import AdminPanelSettingsTwoToneIcon from '@mui/icons-material/AdminPanelSettingsTwoTone'
import ScoreBookLogo from '../Assets/scorebook.png'
import Bookmark from './Bookmark'
import { useAuth } from '../hooks/useAuth'
import { useNavigate, useLocation } from 'react-router-dom'
import { DRAWER_WIDTH, ROUTES } from '../constants'

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}))

export default function Base({ children }) {
  const { deleteTokenCookie, user } = useAuth()
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const disconnect = () => {
    deleteTokenCookie()
    navigate(ROUTES.LOGIN)
  }

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const CustomToolbar = styled(Toolbar)({
    height: 75,
  })

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} elevation={0}>
        <CustomToolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" fontWeight="600">
            ScoreBook
          </Typography>
        </CustomToolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          {open ? (
            <img
              src={ScoreBookLogo}
              alt="Logo de l'applicaton"
              style={{ height: '75px', margin: 'auto' }}
            />
          ) : null}
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        {open ? <Divider sx={{ mb: 2 }} /> : <Box mt={2} />}
        <List>
          <Bookmark
            text={'Mon profil'}
            open={open}
            icon={<AccountCircleTwoToneIcon />}
            action={() => navigate(ROUTES.PROFILE)}
            isActive={location.pathname === ROUTES.PROFILE}
          />
          <Bookmark
            text={'Mes livres'}
            open={open}
            icon={<MenuBookTwoToneIcon />}
            action={() => navigate(ROUTES.HOME)}
            isActive={location.pathname === ROUTES.HOME}
          />
          <Bookmark
            text={'Mes livres à lire'}
            open={open}
            icon={<BookmarkTwoToneIcon />}
            action={() => navigate(ROUTES.TO_READ)}
            isActive={location.pathname === ROUTES.TO_READ}
          />
          <Bookmark
            text={'Statistiques'}
            open={open}
            icon={<TrendingUpTwoToneIcon />}
            action={() => navigate(ROUTES.STATISTICS)}
            isActive={location.pathname === ROUTES.STATISTICS}
          />
          {user && user.is_admin && (
            <Bookmark
              text={'Administration'}
              open={open}
              icon={<AdminPanelSettingsTwoToneIcon />}
              action={() => navigate('/admin')}
              isActive={location.pathname.startsWith('/admin')}
            />
          )}
          <Bookmark
            text={'Se déconnecter'}
            open={open}
            icon={<LogoutTwoToneIcon />}
            action={disconnect}
            isActive={false}
          />
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 4 }, backgroundColor: 'background.default', minHeight: '100vh' }}>
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  )
}
